import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * Numbered buttons shown at once. Past this many pages, rendering every
 * number wraps the control onto a second line — this is the whole reason a
 * window exists rather than `Array.from({ length: totalPages }, ...)`.
 */
const PAGE_WINDOW_SIZE = 10;

/**
 * The up to `PAGE_WINDOW_SIZE` page numbers to show around the current page.
 *
 * The window slides to keep the current page roughly centred rather than
 * jumping in fixed blocks of ten: Next/Previous alone then reveals the next
 * or previous number one at a time as the current page nears either edge of
 * the window, with no separate "jump 10" control needed.
 * Clamped at both ends so a page near 1 or `totalPages` still fills the
 * window instead of centring into a run that runs off the end.
 */
const toVisiblePageNumbers = (page: number, totalPages: number): number[] => {
  if (totalPages <= PAGE_WINDOW_SIZE) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const half = Math.floor(PAGE_WINDOW_SIZE / 2);
  const start = Math.min(
    Math.max(page - half, 1),
    totalPages - PAGE_WINDOW_SIZE + 1,
  );

  return Array.from({ length: PAGE_WINDOW_SIZE }, (_, index) => start + index);
};

/**
 * First/Prev/Last/Next paging for a client-side-paged HCBB table.
 *
 * Shared by the offices table (a provider's own practice locations) and the
 * facility's doctors table — both page an already-fetched, already-filtered
 * list the same way, so the controls are one component rather than two
 * copies drifting apart.
 *
 * First/Prev/Last land on a fixed page, so they stay enabled together at
 * either end; Prev/Next step by one, so each disables on its own end.
 */
export const HcbbTablePagination = ({
  label,
  page,
  totalPages,
  onPageChange,
}: {
  /** Distinguishes this pager from any other on the same page for a screen reader. */
  label: string;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) => {
  const pages = toVisiblePageNumbers(page, totalPages);
  const isFirst = page === 1;
  const isLast = page === totalPages;
  // An ellipsis either side is a hint that First/Last reach further than the
  // window shown — decorative, since those two buttons already say so to a
  // screen reader.
  const hasEarlierPages = pages[0] > 1;
  const hasLaterPages = pages[pages.length - 1] < totalPages;

  return (
    <nav
      aria-label={label}
      className="flex flex-wrap items-center justify-center gap-1 pt-2"
    >
      <Button
        variant="ghost"
        size="sm"
        disabled={isFirst}
        onClick={() => onPageChange(1)}
      >
        First
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Previous page"
        disabled={isFirst}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>

      {hasEarlierPages && (
        <span aria-hidden="true" className="px-1 text-muted-foreground">
          …
        </span>
      )}

      {pages.map((pageNumber) => (
        <Button
          key={pageNumber}
          variant={pageNumber === page ? "default" : "ghost"}
          size="icon"
          aria-label={`Page ${pageNumber}`}
          aria-current={pageNumber === page ? "page" : undefined}
          onClick={() => onPageChange(pageNumber)}
        >
          {pageNumber}
        </Button>
      ))}

      {hasLaterPages && (
        <span aria-hidden="true" className="px-1 text-muted-foreground">
          …
        </span>
      )}

      <Button
        variant="ghost"
        size="icon"
        aria-label="Next page"
        disabled={isLast}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={isLast}
        onClick={() => onPageChange(totalPages)}
      >
        Last
      </Button>
    </nav>
  );
};
